/* Copyright (c) 2017 Backpack Health LLC */
/* global window, navigator, Connection, LocalFileSystem, FileTransfer, SpinnerPlugin, fileChooser, cordova, device, SpinnerDialog, StatusBar */
/* eslint-disable no-underscore-dangle */
import * as _ from 'lodash';
import chroma from 'chroma-js';

import contentDisposition from 'content-disposition';
import { actions as notificationActions } from '../app/redux/ducks/systemNotification';
import store from '../app/redux/reduxStore_authorizedApp';
import Colors from '../app/styles/Colors';
import FileModel from '../models/FileModel';
import { headRequest } from '../apiServices/ApiRequest';
import AppUtils from './AppUtils';

const SIGNUP_PRESENTED_FILE_NAME = '.signup_presented';
const LUMINANCE_THRESHOLD = 0.6;

/* Miscellaneous functions for Mobile */
function isMobileApplication() {
  return !!window.cordova;
}

function isKeyboardOpen() {
  return window.innerHeight < window.screen.height * 0.7;
}

const alertOfflineMode = () => {
  const networkState = IS_ANDROID_BUILD ? navigator.connection.type : 'missing';
  store.dispatch(
    notificationActions.showNotification(`Offline: ${networkState}`, Colors.navDisabled)
  );
};

function isMobileOffline() {
  if (!isMobileApplication()) {
    return false;
  }

  const networkState = navigator.connection.type;
  return window.cordova && Connection.NONE === networkState;
}

function isAbleToMakeRequest() {
  if (isMobileApplication() && isMobileOffline()) {
    alertOfflineMode();
    return false;
  } else {
    return true;
  }
}

function updateStatusBarColor(colorTheme) {
  if (!isMobileApplication()) return;

  const { r, g, b, a } = colorTheme;
  const luminance = chroma(r, g, b, a).luminance();
  const theme = AppUtils.rgbToHex(r, g, b);

  StatusBar.backgroundColorByHexString(theme);
  if (luminance > LUMINANCE_THRESHOLD) {
    StatusBar.styleDefault();
  } else {
    StatusBar.styleLightContent();
  }
}

function setSignupPresented(onSuccess, onFail) {
  window.requestFileSystem(LocalFileSystem.PERSISTENT, 0, fs => {
    fs.root.getFile(
      SIGNUP_PRESENTED_FILE_NAME,
      { create: false, exclusive: false },
      () => {
        onFail();
      },
      () => {
        fs.root.getFile(
          SIGNUP_PRESENTED_FILE_NAME,
          { create: true, exclusive: false },
          () => {
            onSuccess();
          }
        );
      }
    );
  });
}

function removeSignupPresented(callback) {
  window.requestFileSystem(
    LocalFileSystem.PERSISTENT,
    0,
    fs => {
      fs.root.getFile(
        SIGNUP_PRESENTED_FILE_NAME,
        { create: false, exclusive: false },
        fileEntry => {
          fileEntry.remove(callback, callback);
        }
      );
    },
    callback
  );
}

function hideSplashScreen() {
  setTimeout(() => {
    window.navigator.splashscreen.hide();
  }, 300);
}
/* File Chooser and Upload */
function checkValidName(name, type) {
  const fileType = typeof type === 'string' ? type.split('/')[1] : '';
  const validName = name.match(/(.*)(?=\.\w+$)/);
  if (!validName && fileType) {
    return `${name}.${fileType}`;
  } else {
    return name;
  }
}
function checkVideoPermission(onSuccess) {
  const permissions = cordova.plugins.permissions;
  const list = [
    permissions.CAMERA,
    permissions.READ_EXTERNAL_STORAGE,
    permissions.WRITE_EXTERNAL_STORAGE,
  ];

  const error = () => {};
  checkPermissionsAndRequest(list, onSuccess, error, error)
}

function checkPermission(permissionList) {
  const permissions = cordova.plugins.permissions;

  return new Promise((res, rej) => {
    const success = status => {
      if (status.hasPermission) {
        res();
      } else {
        rej('error 2');
      }
    };
    permissions.checkPermission(permissionList, success, () => { rej('error 1') });
  })
}

function checkFilePermissions() {
  const permissions = cordova.plugins.permissions;
  const list = [
    permissions.READ_EXTERNAL_STORAGE,
    permissions.WRITE_EXTERNAL_STORAGE,
  ];
  return checkPermission(list);
}

function checkPermissionsAndRequest(permissionList, onSuccess, onDecline, onDeclinedForever) {
  const beforeRequest = new Date();

  requestPermissions(permissionList)
    .then(() => {
      onSuccess();
    })
    .catch(() => {
      // hack. no way to know if they reject request from modal or have denied all future
      // so if it gets returned superfast, it probably did not come from the modal
      const alreadyDeniedRequest = (new Date() - beforeRequest) < 350;
      if (alreadyDeniedRequest) {
        onDeclinedForever();
      } else {
        onDecline();
      }
    });
}

function requestPermissions(permissionList) {
  const permissions = cordova.plugins.permissions;
  return new Promise((res, rej) => {
    permissions.requestPermissions(
      permissionList,
      status => {
        if (status.hasPermission) {
          res();
        } else {
          rej()
        }
      },
      rej
    );
  })
}

function checkAndRequestPhotoCameraPermissions(onSuccess, onDecline, onDeclinedForever) {
  if (!IS_ANDROID_BUILD) { onSuccess(); return; }

  const permissions = cordova.plugins.permissions;
  const permissionList = [
    permissions.READ_EXTERNAL_STORAGE,
    permissions.WRITE_EXTERNAL_STORAGE,
    permissions.CAMERA,
  ];
  return checkPermissionsAndRequest(permissionList, onSuccess, onDecline, onDeclinedForever);
}

function checkAndRequestVideoCameraPermissions(onSuccess, onDecline, onDeclinedForever) {
  if (!IS_ANDROID_BUILD) { onSuccess(); return; }

  const permissions = cordova.plugins.permissions;
  const permissionList = [
    permissions.READ_EXTERNAL_STORAGE,
    permissions.WRITE_EXTERNAL_STORAGE,
    permissions.CAMERA,
    permissions.RECORD_AUDIO,
  ];
  return checkPermissionsAndRequest(permissionList, onSuccess, onDecline, onDeclinedForever);
}

function checkAndRequestFilePermissions(onSuccess, onDecline, onDeclinedForever) {
  if (!IS_ANDROID_BUILD) { onSuccess(); return; }

  const permissions = cordova.plugins.permissions;
  const permissionList = [
    permissions.READ_EXTERNAL_STORAGE,
    permissions.WRITE_EXTERNAL_STORAGE,
  ];
  return checkPermissionsAndRequest(permissionList, onSuccess, onDecline, onDeclinedForever);
}

function showSystemSpinner() {
  if (!IS_ANDROID_BUILD) { return; }
  SpinnerDialog.show();
}

function hideSystemSpinner() {
  if (!IS_ANDROID_BUILD) { return; }
  SpinnerDialog.hide();
}

function selectFileFromSource(defaultName, source, onError, onSuccess) {
  let googleFileName = null;
  const preparedFileForUpload = file => {
    file.file(
      convertFileToEntry => {
        if (convertFileToEntry.size > 100000000) {
          SpinnerDialog.hide();
          onError();
        } else {
          const fileType = convertFileToEntry.type || 'application/octet-stream';
          SpinnerDialog.show();
          const reader = new window.FileReader();
          reader.onloadend = (evt) => {
            const imgBlob = new window.Blob([evt.target.result], {
              type: fileType,
            });
            const filename =
              source === 'library'
                ? checkValidName(file.name, fileType)
                : checkValidName(defaultName, fileType);
            imgBlob.name = googleFileName || filename;
            onSuccess(imgBlob);
          };
          reader.onerror = () => {};
          reader.readAsArrayBuffer(convertFileToEntry);
        }
      },
      () => {}
    );
  };
  const captureError = function captureError() {
    SpinnerDialog.hide();
  };
  const captureErrorFirst = function captureError() {
    SpinnerDialog.hide();
  };
  const captureSuccess = function captureSuccess(mediaFiles) {
    if (mediaFiles) {
      const path = mediaFiles.url || mediaFiles[0].fullPath || mediaFiles;
      const isContentPath = path.includes('content://');
      const convertPath = !path.includes(
        'com.google.android.apps.docs.storage.legacy'
      ) && !path.includes(
        'com.android.contacts.downloads.documents'
      );
      if (isContentPath && convertPath) {
        window.FilePath.resolveNativePath(path, captureSuccess, captureError);
      } else if (!convertPath) {
        window.plugins.DocumentContract.getContract(
          { uri: path },
          contract => {
            if (contract.size > 100000000) {
              onError();
            } else {
              googleFileName = contract._display_name;
              window.resolveLocalFileSystemURL(path, preparedFileForUpload);
            }
          },
          () => {}
        );
      } else {
        const extract = path.split('/');
        const encode = encodeURIComponent(extract[extract.length - 1]);
        const combine = _.reduce(
          extract,
          (abs, s, i) => {
            if (i === extract.length - 1) {
              return `${abs}/${encode}`;
            } else {
              return `${abs}/${s}`;
            }
          },
          ''
        );
        window.resolveLocalFileSystemURL(
          combine.slice(1),
          preparedFileForUpload
        );
      }
    }
  };
  if (source === 'library') {
    const permissions = cordova.plugins.permissions;

    checkPermission([permissions.READ_EXTERNAL_STORAGE])
      .then(() => {
        fileChooser.open(captureSuccess, captureError, '*/*');
      })
      .catch(() => {
        permissions.requestPermissions(
          [permissions.READ_EXTERNAL_STORAGE],
          secondCheck => {
            if (secondCheck.hasPermission) {
              fileChooser.open(captureSuccess, captureError, '*/*');
            }
          },
          captureErrorFirst
        );
      });
  } else if (source === 'image') {
    navigator.camera.getPicture(captureSuccess, captureError, {
      destinationType: window.Camera.DestinationType.FILE_URI,
      sourceType: window.Camera.PictureSourceType.CAMERA,
      correctOrientation: true,
    });
  } else if (source === 'video') {
    const callback = () => {
      navigator.device.capture.captureVideo(captureSuccess, captureError, {
        limit: 1,
      });
    };
    if (parseFloat(device.version) >= 6.0) {
      checkVideoPermission(callback);
    } else {
      callback();
    }
  }
}

/* Miscellaneous */
function getCacheFileFromURL(file, url, intl, skipCache, onSuccess, onCache) {
  let path = null;
  const fileTransfer = new FileTransfer();
  if (file && file.getName) {
    const name = file.getName();
    path = `${
      window.cordova.file.externalDataDirectory
    }/${encodeURIComponent(name)}`;
  } else {
    path = `${
      window.cordova.file.externalDataDirectory
    }/${encodeURIComponent(file)}`;
  }
  const fileExist = entry => onSuccess(entry);
  const fetchData = cache => {
    const options = { dimBackground: true };
    const sharingMessage = intl.formatMessage({ id: 'shareCardPDF.prepareFileSharing.message' });
    SpinnerPlugin.activityStart(sharingMessage, options);
    fileTransfer.download(
      url,
      path,
      entry => {
        SpinnerPlugin.activityStop();
        onSuccess(entry);
      },
      () => {
        SpinnerPlugin.activityStop();
        onCache(cache);
      },
      true,
      { encodeURI: false }
    );
  };

  if (skipCache) {
    fetchData();
  } else {
    window.resolveLocalFileSystemURL(path, fileExist, fetchData);
  }
}

function mobileShareFile(file, url, intl, skipCache = false, onSuccess = () => {}, onError = () => {}) {
  getCacheFileFromURL(
    file,
    url,
    intl,
    skipCache,
    entry => {
      window.cordova.plugins.MMS.send(
        entry.name,
        file.getMimeType(),
        onSuccess,
        onError
      );
    },
    onError
  );
}

function mobileShareUrl(url) {
  navigator.share(url);
}

function checkValidFileName(name, type) {
  const fileType = type.split('/')[1];
  const validName = name.match(/(.*)(?=\.\w+$)/);
  if (!validName) {
    return `${name}.${fileType}`;
  } else {
    return name;
  }
}

function convertToBlob(
  file,
  googleFileName = null,
  onSuccess = () => {},
  onError = () => {}
) {
  file.file(
    convertFileToEntry => {
      const reader = new window.FileReader();
      reader.onloadend = (evt) => {
        const imgBlob = new window.Blob([evt.target.result], {
          type: convertFileToEntry.type,
        });
        imgBlob.name =
          googleFileName ||
          checkValidFileName(file.name, convertFileToEntry.type);
        const newFile = FileModel.createFromRawFile(imgBlob);
        if (newFile.isTooBig()) {
          onError();
        } else {
          onSuccess(imgBlob);
        }
      };
      reader.onerror = () => {};
      reader.readAsArrayBuffer(convertFileToEntry);
    },
    () => {}
  );
}

function headerRequest(url, callback) {
  headRequest(url).end((err, res) => {
    // if (!err || res.ok) {
    const disposition = res.header['content-disposition'];
    const parse = contentDisposition.parse(disposition);
    const filename = _.get(parse, 'parameters.filename', null);
    if (filename) callback(filename);
    // }
  });
}

function openInAppBrowser(url) {
  cordova.InAppBrowser.open(url, '_system', 'location=yes');
}

function lockOrientation(orientationLockName) {
  if (!IS_ANDROID_BUILD) { return; }
  window.screen.orientation.lock(orientationLockName);
}

function unlockOrientation() {
  if (!IS_ANDROID_BUILD) { return; }
  window.screen.orientation.unlock();
}


function requestPhotoLibraryPermission() {
  return new Promise((res, rej) => {
    cordova.plugins.photoLibrary.requestAuthorization(
      res,
      rej,
      {
        read: true,
        write: true,
      },
    );
  });
}

function saveImageToPhotoGallery(uri, albumName) {
  return new Promise((res, rej) => {
    cordova.plugins.photoLibrary.saveImage(
      uri,
      albumName,
      res,
      rej,
    );
  });
}

function goToSettings(name) {
  cordova.plugins.settings.open(name, () => {}, () => {});
}

function hideStatusBar() {
  if (!IS_ANDROID_BUILD) { return; }
  StatusBar.hide();
}

function showStatusBar() {
  if (!IS_ANDROID_BUILD) { return; }
  StatusBar.show();
}


export {
  updateStatusBarColor,
  setSignupPresented,
  removeSignupPresented,
  hideSplashScreen,
  mobileShareFile,
  mobileShareUrl,
  getCacheFileFromURL,
  /* Prepare Upload */
  selectFileFromSource,
  /* Miscellaneous */
  alertOfflineMode,
  isMobileOffline,
  isMobileApplication,
  isAbleToMakeRequest,
  isKeyboardOpen,
  checkPermission,
  checkFilePermissions,
  requestPermissions,
  checkAndRequestFilePermissions,
  checkAndRequestPhotoCameraPermissions,
  checkAndRequestVideoCameraPermissions,
  lockOrientation,
  unlockOrientation,
  showSystemSpinner,
  hideSystemSpinner,
  goToSettings,
  hideStatusBar,
  showStatusBar,
  /* File Validation */
  checkValidName,
  convertToBlob,
  headerRequest,
  openInAppBrowser,
  /* Photo Library Specific*/
  requestPhotoLibraryPermission,
  saveImageToPhotoGallery,
};
